import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeSpec } from './NodeSpec';
import type { NodeCfg, NodeSocketKind } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { defineControlOpts } from '../../controls/FlowNodeControl';

const materialLookupProperties = {
    materialId: { socket: 'string', label: 'Material ID' },
    modelNumber: { socket: 'string', label: 'Model Number' },
    className: { socket: 'string', label: 'Class Name' },
    materialStatus: { socket: 'string', label: 'Material Status' },
    openConcernCount: { socket: 'number', label: 'Open Concern Count' },
    closedConcernCount: { socket: 'number', label: 'Closed Concern Count' },
    latestConcernText: { socket: 'string', label: 'Latest Concern Text' },
    incompleteProcessCount: { socket: 'number', label: 'Incomplete Process Count' },
    completedProcessCount: { socket: 'number', label: 'Completed Process Count' },
    expectedProcessCount: { socket: 'number', label: 'Expected Process Count' },
    incompleteWorkInstructionCount: { socket: 'number', label: 'Incomplete Work Instruction Count' },
    completedWorkInstructionCount: { socket: 'number', label: 'Completed Work Instruction Count' },
    linkedWorkInstructionCount: { socket: 'number', label: 'Linked Work Instruction Count' },
} as const satisfies Record<string, { socket: NodeSocketKind; label: string }>;

const materialLookupInitialWidth = Math.max(
    180,
    ...Object.values(materialLookupProperties).map(property => (property.label.length * 8) + 48)
);

type MaterialLookupProperty = keyof typeof materialLookupProperties;

type EnabledProperties = NonNullable<NodeCfg<'materialLookup'>['controlValues']>['enabledProperties'];

const getEnabledProperties = (): EnabledProperties => Object.fromEntries(Object.keys(materialLookupProperties).map(property => [ property, true ]));

const isPropertyEnabled = (nodeCfg: NodeCfg<'materialLookup'>, property: MaterialLookupProperty): boolean => nodeCfg.controlValues?.enabledProperties?.[property] ?? true;

export const materialLookupUiNodeSpec: NodeSpec<NodeCfg<'materialLookup'>> = {
    cfg: () => ({
        id: getUID(),
        kind: 'materialLookup',
        width: materialLookupInitialWidth,
        controlValues: {
            enabledProperties: getEnabledProperties(),
        },
    }),
    controls: nodeCfg => {
        const disabledProperties = (Object.keys(materialLookupProperties) as MaterialLookupProperty[])
            .filter(property => !isPropertyEnabled(nodeCfg, property));
        if (!disabledProperties.length) {
            return {};
        }
        return {
            enabledProperties: defineControlOpts({
                cfg: {
                    kind: 'selection',
                    placeholder: 'Add Property',
                    items: disabledProperties.map(property => ({
                        label: materialLookupProperties[property].label,
                        value: property,
                    })),
                },
                value: nodeCfg.controlValues?.enabledProperties ?? getEnabledProperties(),
                on: {
                    'update:value': (value, { flowEventEmitter }) => {
                        if (typeof value !== 'string' || !(value in materialLookupProperties)) {
                            return;
                        }
                        const property = value as MaterialLookupProperty;
                        flowEventEmitter.emit('updateNodeCfg', {
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...(nodeCfg.controlValues ?? {}),
                                    enabledProperties: {
                                        ...(nodeCfg.controlValues?.enabledProperties ?? getEnabledProperties()),
                                        [property]: true,
                                    },
                                },
                            },
                        });
                    },
                },
            }),
        };
    },
    inputs: () => ({
        trigger: { socket: 'event', label: 'Trigger' },
        serialNumber: { socket: 'string', label: 'Serial Number' },
    }),
    nodeCategory: 'material',
    kindLabel: 'Material Lookup',
    outputs: nodeCfg => {
        const outputs: Record<string, { socket: NodeSocketKind; label?: string; controlOpts?: ReturnType<typeof defineControlOpts> }> = {
            next: { socket: 'event', label: 'Next' },
            ...materialLookupProperties,
        };
        for (const property of Object.keys(materialLookupProperties) as MaterialLookupProperty[]) {
            if (isPropertyEnabled(nodeCfg, property)) {
                const propertyCfg = materialLookupProperties[property];
                outputs[property] = {
                    socket: propertyCfg.socket,
                    controlOpts: defineControlOpts({
                        cfg: {
                            kind: 'deletableSocket',
                            label: propertyCfg.label,
                            rightAlign: true,
                        },
                        value: null,
                        enableWithConnectedSocket: true,
                        on: {
                            'update:value': () => {}, // no-op, this control does not use the update:value event
                            'delete': (_, { flowEventEmitter }) => {
                                flowEventEmitter.emit('removeNodeSocket', {
                                    newNodeCfg: {
                                        ...nodeCfg,
                                        controlValues: {
                                            ...(nodeCfg.controlValues ?? {}),
                                            enabledProperties: {
                                                ...(nodeCfg.controlValues?.enabledProperties ?? getEnabledProperties()),
                                                [property]: false,
                                            },
                                        },
                                    },
                                    direction: 'output',
                                    removalMethod: {
                                        kind: 'uniqueIdentifier',
                                        value: property,
                                    },
                                });
                            },
                        },
                    }),
                };
            } else {
                delete outputs[property];
            }
        }
        return outputs;
    },
    requiredSockets: (node, _, availableOutputSockets) => ({
        required: [ 'serialNumber', 'trigger', 'next' ],
        conditionallyRequired: [
            {
                kind: 'or',
                availableOptions: availableOutputSockets.filter(s => ![ 'serialNumber', 'trigger', 'next' ].includes(s)),
            },
        ],
    }),
    validator: () => true,
};
